import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getCajaMovementsByLocalAndDateRange } from '../../../../../../crud/payments_api/caja_movements';
import { getUsersByLocalId } from '../../../../../../crud/users_api/users';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import Table from './components/Table/Table';
import FilterBar from './components/FilterBar/FilterBar';
import { Form, Offcanvas, Button } from 'react-bootstrap';
import MultiSelectFilter from '../../../../../../components/common/MultiSelectFilter/MultiSelectFilter';
import { FaFilter } from 'react-icons/fa';
import { X } from 'react-bootstrap-icons';
import DateFilter from '../../../Receipts/components/Filters/DateFilter';
import { exportFilteredMovimientos } from './utils/movimientosExcelExport';
import { LocalSession } from '../../../../../../types/LocalSession';
interface Option {
  label: string;
  value: number | string;
}



interface CajaMovement {
  caja_movement_id: number;
  workstation_id: number;
  workstation_name: string;
  user_id: number;
  user_name: string;
  before_amount: number;
  after_amount: number;
  amount: number;
  description: string | null;
  is_positive: boolean;
  caja_movement_type_id: number;
  caja_movement_type_name: string;
  caja_movement_metadata: any;
  timestamp: string;
  local_timestamp: string;
  session_id?: number;
  shift_id?: number | null;
  shift_name?: string | null;
  receipt_id?: number | null;
}

interface HistorialMovimientosProps {
  lastSession?: LocalSession | null;
}

const HistorialMovimientos: React.FC<HistorialMovimientosProps> = ({ lastSession }) => {
  const [data, setData] = useState<CajaMovement[]>([]);
  const [filteredData, setFilteredData] = useState<CajaMovement[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Mobile filter state
  const [showFilters, setShowFilters] = useState(false);
  
  // Date filters
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  // Alineado con Receipts: empieza desactivado y se filtra client-side sin tocar fechas
  const [soloSesionActual, setSoloSesionActual] = useState(false);
  
  // Filter states
  const [selectedUsers, setSelectedUsers] = useState<(number | string)[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<(number | string)[]>([]);
  const [enableAmountRange, setEnableAmountRange] = useState(false);
  const [amountRange, setAmountRange] = useState<[number, number]>([0, 10000]);
  
  // Options for filters
  const [userOptions, setUserOptions] = useState<Option[]>([]);
  const [typeOptions, setTypeOptions] = useState<Option[]>([]);
  const [cajaOptions, setCajaOptions] = useState<Option[]>([]);

  // Convert typeOptions to the FilterOption format
  const transactionTypeOptions = typeOptions.map(option => ({
    label: option.label,
    value: option.value
  }));

    // Filter options removed - actions dropdown not needed

  // Convert cajaOptions to FilterOption format
  const cajaFilterOptions = cajaOptions.map(option => ({
    label: option.label,
    value: option.value
  }));  // Get localId from lastSession
  const localId = lastSession?.local_id || 0;

  // Convert userOptions to FilterOption format
  const userFilterOptions = userOptions.map(option => ({
    label: option.label,
    value: option.value
  }));

  // Initialize dates (igual que Receipts): 2 días atrás hasta mañana
  useEffect(() => {
    const now = new Date();
    const twoDaysAgo = new Date(now);
    twoDaysAgo.setDate(now.getDate() - 2);
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    setStartDate(twoDaysAgo.toISOString().split('T')[0]);
    setEndDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  // (Receipts parity) No sincronizamos fechas al activar/desactivar soloSesionActual

  // Load data when component becomes visible or dates change
  useEffect(() => {
    if (!localId || !startDate || !endDate) return;

    setLoading(true);
    getCajaMovementsByLocalAndDateRange(localId, startDate, endDate)
      .then((res: any) => {
        const validMovements = res.data.filter((m: CajaMovement) => {
          return m.local_timestamp && m.local_timestamp !== '' && !isNaN(new Date(m.local_timestamp).getTime());
        });
        
        setData(validMovements.reverse());
        setFilteredData(validMovements.reverse());

        // Extract unique types and users
        const types = Array.from(new Set(validMovements.map((m: CajaMovement) => m.caja_movement_type_name)))
          .filter((type): type is string => typeof type === 'string' && type.length > 0)
          .map((type, index) => ({ label: type, value: type })); // Use type name as value for easier filtering
        setTypeOptions(types);

        // Extract unique cajas
        const cajas = Array.from(new Set(validMovements.map((m: CajaMovement) => m.workstation_name)))
          .filter((caja): caja is string => typeof caja === 'string' && caja.length > 0)
          .map((caja, index) => ({ label: caja, value: index + 1 }));
        setCajaOptions(cajas);

        // Get users
        getUsersByLocalId(localId).then((userRes: any) => {
          const users = userRes.data
            .filter((user: any) => {
              // Ensure user has valid id and some form of name
              return user && 
                     (user.id || user.user_id) && 
                     (user.name || user.username || user.user_name);
            })
            .map((user: any) => ({
              label: user.name && user.lastname 
                ? `${user.name} ${user.lastname}` 
                : user.username || user.user_name || `Usuario ${user.id || user.user_id}`,
              value: user.id || user.user_id
            }));
          setUserOptions(users);
        }).catch((userErr: any) => {
          console.error('Error getting users:', userErr);
          // Set empty array if user fetch fails
          setUserOptions([]);
        });
      })
      .catch((err: any) => {
        console.error('Error obteniendo movimientos:', err);
        toast.error('No se pudieron cargar movimientos');
      })
      .finally(() => setLoading(false));
  }, [localId, startDate, endDate]);

  // Helper de depuración manual (sin logs automáticos). Ejecutar en consola: window.__debugSessionMods()
  useEffect(() => {
    (window as any).__debugSessionMods = () => {
      const counts: Record<string, number> = {};
      data.forEach(m => {
        const sid = String((m as any).session_id ?? (m as any).local_session_id ?? 'none');
        counts[sid] = (counts[sid] || 0) + 1;
      });
      return counts;
    };
    return () => { delete (window as any).__debugSessionMods; };
  }, [data, lastSession, soloSesionActual]);

  // Apply filters
  const applyFilters = useCallback((
    movements: CajaMovement[],
    users: (number | string)[],
    types: (number | string)[],
    range: [number, number],
    currentSession: boolean,
    enableRange: boolean
  ): CajaMovement[] => {
    let filtered = [...movements];

    // Filtro de sesión actual (igual a Receipts): filtrar por session_id/local_session_id si existe
    if (currentSession && lastSession?.local_session_id) {
      const targetSessionId = lastSession.local_session_id;
      filtered = filtered.filter(m => {
        const movementSessionId = (m as any).session_id ?? (m as any).local_session_id;
        return movementSessionId === targetSessionId;
      });
    }

    // Filter by users
    if (users.length > 0) {
      filtered = filtered.filter(m => users.includes(m.user_id));
    }

    // Filter by types
    if (types.length > 0) {
      filtered = filtered.filter(m => types.includes(m.caja_movement_type_name));
    }

    // Filter by amount range
    if (enableRange && range[0] !== range[1]) {
      filtered = filtered.filter(m => {
        const amount = Math.abs(m.amount);
        return amount >= range[0] && amount <= range[1];
      });
    }

    // Ensure final ordering: most recent first
    filtered.sort((a: CajaMovement, b: CajaMovement) => {
      const dateA = new Date(a.local_timestamp || a.timestamp).getTime();
      const dateB = new Date(b.local_timestamp || b.timestamp).getTime();
      return dateB - dateA;
    });

    return filtered;
  }, [lastSession]);

  // No-op effect removed: applyFilters is used directly when rendering the Table

  const handleStartDateChange = (newDate: string) => {
    // No desactivar soloSesionActual (paridad con Receipts)
    setStartDate(newDate);
  };
  
  const handleEndDateChange = (newDate: string) => {
    // No desactivar soloSesionActual (paridad con Receipts)
    setEndDate(newDate);
  };

  // Handle Excel export
  const handleExportToExcel = () => {
    const filteredMovements = applyFilters(
      filteredData, 
      selectedUsers, 
      selectedTypes, 
      amountRange, 
      soloSesionActual, 
      enableAmountRange
    );
    
    const success = exportFilteredMovimientos(filteredMovements, startDate, endDate);
    
    if (success) {
      toast.success('Movimientos exportados exitosamente');
    } else {
      toast.error('No hay movimientos para exportar');
    }
  };

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 200px)' }}>
      <div className="flex items-center gap-3 mb-3 lg:hidden">
        <button className="mp-toolbar-btn" onClick={() => setShowFilters(true)} aria-label="Abrir filtros">
          <FaFilter />
        </button>
      </div>

      {/* FilterBar-style horizontal filters */}
      <div className="hidden lg:block mt-4 mb-4">
          <FilterBar
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            soloSesionActual={soloSesionActual}
            setSoloSesionActual={setSoloSesionActual}
            cajaOptions={cajaFilterOptions}
            selectedCajas={[]}
            onCajasChange={() => {}} // Will implement if needed
            userOptions={userFilterOptions}
            selectedUsers={userFilterOptions.filter(user => selectedUsers.includes(user.value))}
            onUsersChange={(selected) => setSelectedUsers(selected.map(s => s.value))}
            transactionTypeOptions={transactionTypeOptions}
            selectedTransactionTypes={transactionTypeOptions.filter(type => selectedTypes.includes(type.value))}
            onTransactionTypesChange={(selected) => setSelectedTypes(selected.map(s => s.value))}
            enableAmountRange={enableAmountRange}
            setEnableAmountRange={setEnableAmountRange}
            amountRange={amountRange}
            setAmountRange={setAmountRange}
            activeFilters={[]}
            onRemoveFilter={() => {}}
            onClearAllFilters={() => {}}
            onExportToExcel={handleExportToExcel}
            exportDisabled={loading}
            totalMovements={applyFilters(filteredData, selectedUsers, selectedTypes, amountRange, soloSesionActual, enableAmountRange).length}
          />
      </div>

      {/* Offcanvas para móvil - reuse mp-offcanvas styles and group filters into one section */}
      <Offcanvas
        show={showFilters}
        onHide={() => setShowFilters(false)}
        placement="end"
        className="mp-offcanvas"
        backdrop={true}
        scroll={false}
      >
        <Offcanvas.Header className="offcanvas-header">
          <div className="header-content">
            <Offcanvas.Title className="offcanvas-title">
              Opciones de Filtrado
            </Offcanvas.Title>
            <div className="section-indicator">Historial de Movimientos</div>
          </div>

          <Button
            variant="none"
            className="close-button"
            onClick={() => setShowFilters(false)}
            aria-label="Cerrar filtros"
          >
            <X size={24} />
          </Button>
        </Offcanvas.Header>

        <Offcanvas.Body className="offcanvas-body">
          <div className="filters-container">
            <div className="filter-section">
              <h6 className="filter-section-title">Filtros Generales</h6>

              <div className="filter-group">
                <Form.Label className="filter-label">Rango de fechas</Form.Label>
                <DateFilter
                  startDate={startDate}
                  setStartDate={handleStartDateChange}
                  endDate={endDate}
                  setEndDate={handleEndDateChange}
                />
              </div>

              <div className="filter-group">
                <div className="multiselect-container">
                  <MultiSelectFilter
                    options={userFilterOptions}
                    onChange={(selected) => setSelectedUsers(selected.map(s => s.value))}
                    placeholder="Filtrar por Usuarios"
                  />
                </div>

                <div className="multiselect-container" style={{ marginTop: '0.5rem' }}>
                  <MultiSelectFilter
                    options={transactionTypeOptions}
                    onChange={(selected) => setSelectedTypes(selected.map(s => s.value))}
                    placeholder="Filtrar por Tipo de Movimiento"
                  />
                </div>

                {cajaFilterOptions.length > 1 && (
                  <div className="multiselect-container" style={{ marginTop: '0.5rem' }}>
                    <MultiSelectFilter
                      options={cajaFilterOptions}
                      onChange={() => {}} // Will implement if needed
                      placeholder="Filtrar por Caja"
                    />
                  </div>
                )}
              </div>

              <div className="filter-group">
                <div className="checkbox-filter">
                  <input
                    type="checkbox"
                    id="soloSesionActualMobile"
                    checked={soloSesionActual}
                    onChange={e => setSoloSesionActual(e.target.checked)}
                  />
                  <label htmlFor="soloSesionActualMobile">Solo sesión actual</label>
                </div>
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <div className="flex-1 flex flex-col min-h-0">
        {loading ? (
          <LoadingSpinner text="Cargando movimientos..." transparent />
        ) : (
          <Table data={applyFilters(filteredData, selectedUsers, selectedTypes, amountRange, soloSesionActual, enableAmountRange)} />
        )}
      </div>
    </div>
  );
};

export default HistorialMovimientos;
